import type { ReactNode } from 'react'
import AsyncSubmitButton from '@/components/shared/AsyncSubmitButton'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/utilities/ui'
import { X } from 'lucide-react'

type ConfirmationAlertDialogProps = {
  open: boolean
  title: ReactNode
  description: ReactNode
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onCancel: () => void
  onConfirm: () => void
  confirmText: string
  cancelText?: string
  confirmDisabled?: boolean
  confirmDanger?: boolean
  confirmAriaLabel?: string
  closeAriaLabel?: string
  errorMessage?: string | null
  testIdPrefix: string
  contentTestId?: string
  contentClassName?: string
  headerClassName?: string
  titleClassName?: string
  descriptionClassName?: string
  footerClassName?: string
  cancelButtonClassName?: string
  confirmButtonClassName?: string
  showCloseButton?: boolean
  variant?: 'confirm' | 'acknowledge'
}

export default function ConfirmationAlertDialog({
  open,
  title,
  description,
  isSubmitting = false,
  onOpenChange,
  onCancel,
  onConfirm,
  confirmText,
  cancelText = 'Cancel',
  confirmDisabled = false,
  confirmDanger = false,
  confirmAriaLabel,
  closeAriaLabel = 'Close confirmation modal',
  errorMessage = null,
  testIdPrefix,
  contentTestId,
  contentClassName,
  headerClassName,
  titleClassName,
  descriptionClassName,
  footerClassName,
  cancelButtonClassName,
  confirmButtonClassName,
  showCloseButton = true,
  variant = 'confirm',
}: Readonly<ConfirmationAlertDialogProps>) {
  const isAcknowledge = variant === 'acknowledge'
  const isStringDescription = typeof description === 'string' || typeof description === 'number'

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className={cn('max-w-152 rounded-4xl px-14 py-14', contentClassName)}
        data-testid={contentTestId ?? `${testIdPrefix}-modal`}
      >
        <AlertDialogHeader className={cn('relative text-center mb-2', headerClassName)}>
          <AlertDialogTitle
            className={cn('font-medium! text-xl! text-dark-blue text-center', titleClassName)}
          >
            {title}
          </AlertDialogTitle>
          {showCloseButton ? (
            <button
              type="button"
              className="absolute -right-2 -top-4 cursor-pointer disabled:cursor-not-allowed"
              onClick={onCancel}
              disabled={isSubmitting}
              aria-label={closeAriaLabel}
              data-testid={`${testIdPrefix}-close-button`}
            >
              <X className="w-6 h-6" />
            </button>
          ) : null}
        </AlertDialogHeader>
        {isStringDescription ? (
          <AlertDialogDescription
            className={cn(
              'whitespace-pre-line text-base text-black text-center mb-4',
              descriptionClassName,
            )}
          >
            {description}
          </AlertDialogDescription>
        ) : (
          <AlertDialogDescription
            className={cn('text-base text-black text-center mb-4', descriptionClassName)}
            asChild
          >
            <div>{description}</div>
          </AlertDialogDescription>
        )}
        {errorMessage ? (
          <div className="text-error text-sm text-center" data-testid={`${testIdPrefix}-error`}>
            {errorMessage}
          </div>
        ) : null}
        <AlertDialogFooter
          className={cn(
            'w-full items-center justify-center sm:justify-center gap-4',
            footerClassName,
          )}
        >
          {!isAcknowledge && (
            <AsyncSubmitButton
              onSubmit={onCancel}
              primary={false}
              text={cancelText}
              disabled={isSubmitting}
              customStyles={isSubmitting ? 'text-cp-grey!' : ''}
              className={cn('w-full max-w-48', cancelButtonClassName)}
              data-testid={`${testIdPrefix}-cancel-button`}
            />
          )}
          <AsyncSubmitButton
            onSubmit={onConfirm}
            primary
            danger={confirmDanger}
            text={confirmText}
            loading={isSubmitting}
            disabled={confirmDisabled || isSubmitting}
            className={cn('w-full max-w-48', confirmButtonClassName)}
            data-testid={`${testIdPrefix}-confirm-button`}
            ariaLabel={confirmAriaLabel}
          />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
